import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StandardDiagnosticCodeForm } from "../components/StandardDiagnosticCodeForm";
import { fetchAdminDiagnosis, updateAdminDiagnosis } from "../api/adminApi";
import { getAuthToken } from "../auth";
import {
  getStandardDiagnosticCodeById,
  loadStandardDiagnosticCodes,
  updateStandardDiagnosticCode,
} from "../standardDiagnosticCodesStorage";
import type { StandardDiagnosticCode } from "../types";

type EditStandardDiagnosticCodePageProps = {
  diagnosticId: string;
};

export function EditStandardDiagnosticCodePage({ diagnosticId }: EditStandardDiagnosticCodePageProps) {
  const { t } = useTranslation();
  const labels = t.safecheck.acts.standardDiagnostics.edit;
  const { showToast } = useToast();
  const [diagnostic, setDiagnostic] = useState<StandardDiagnosticCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        if (getAuthToken()) {
          const apiDiagnosis = await fetchAdminDiagnosis(Number(diagnosticId));
          if (cancelled) return;
          setDiagnostic({
            id: String(apiDiagnosis.id),
            code: apiDiagnosis.code,
            label: apiDiagnosis.label || apiDiagnosis.name,
            type: apiDiagnosis.type || "",
            description: apiDiagnosis.description || "",
          });
        } else {
          setDiagnostic(getStandardDiagnosticCodeById(diagnosticId) ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Impossible de charger le diagnostic.");
          setDiagnostic(getStandardDiagnosticCodeById(diagnosticId) ?? null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [diagnosticId]);

  if (loading) {
    return <p className="text-sm text-muted">Chargement…</p>;
  }

  if (!diagnostic) {
    return (
      <div className="theme-card p-6 text-sm text-muted-foreground">
        {error ?? labels.notFound}
        <Link to="/dashboard/acts/standard-diagnostics" className="ml-2 text-primary hover:underline">
          {t.safecheck.back}
        </Link>
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.acts },
          { label: t.safecheck.acts.standardDiagnostics.title, href: "/dashboard/acts/standard-diagnostics" },
          { label: labels.title },
        ]}
      />

      <div className="mb-4 flex justify-end">
        <Link
          to="/dashboard/acts/standard-diagnostics"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={14} />
          {t.safecheck.back}
        </Link>
      </div>

      <div className="theme-card p-6">
        <div className="mb-6 flex items-center gap-2 text-base font-semibold text-foreground">
          <Pencil size={16} className="text-yellow" />
          {labels.cardTitle}
        </div>

        <StandardDiagnosticCodeForm
          labels={labels}
          cancelHref="/dashboard/acts/standard-diagnostics"
          initialValues={diagnostic}
          onSubmit={async (values) => {
            if (!getAuthToken()) {
              updateStandardDiagnosticCode(loadStandardDiagnosticCodes(), diagnosticId, values);
              showToast(t.actions.saved);
              return;
            }
            await updateAdminDiagnosis(Number(diagnosticId), {
              code: values.code,
              label: values.label,
              type: values.type,
              description: values.description,
            });
            showToast(t.actions.saved);
          }}
        />
      </div>
    </>
  );
}
